/**
 * Derived relations — render-time "virtual kids" from non-native linkage
 * applogs, so pipeline output (e.g. daemon-structured transcripts) shows up
 * as children of its source entity WITHOUT the producer knowing note3's
 * relation model. The applog thread is the pipe; attribute namespaces are
 * the interface — this module is where note3 joins them into the tree.
 *
 * Semantics (decided with manu, 2026-07-10):
 * - A block renders as derived kid of an anchor while it has NO real parent
 *   relation in the current thread. Real relations trump automatically:
 *   the moment one exists (in-app structure flow writes one; or the user
 *   adopts/moves the block), derivation stops for that block.
 * - Structural interaction requires adoption (creating a real relation) —
 *   see the "Adopt as child" action in BulletMenu.
 * - Anchors don't have to be real blocks — any rendered entity (e.g. a
 *   virtual-root asset entity) gets its derived kids via TreeItem.
 *
 * v1 provider: structured transcripts —
 *   root —structuredTranscript/sourceEntity→ trx
 *   trx  —transcription/recording (canonical) | transcription/sourceEntity (daemon legacy)→ anchor
 *
 * All collections are liveEntityCollection subscriptions memoized per
 * (thread, pattern, attrs) — shared across every TreeItem instance.
 *
 * Non-reactive counterpart (exports/share walks, worker-safe):
 * ./derived-relations-core.ts — KEEP THE CHAIN IN SYNC.
 */

import type { EntityID } from '@wovin/core/applog'
import type { ThreadOnlyCurrentNoDeleted } from '@wovin/core/thread'
import type { Accessor } from 'solid-js'
import { useLiveEntityCollection } from '@wovin/hooks-solid'
import { createMemo } from 'solid-js'

export interface DerivedKidsProvider {
	name: string
	/** Reactive list of derived kid block IDs for an anchor entity. */
	useDerivedKids: (anchorID: EntityID, thread: ThreadOnlyCurrentNoDeleted) => Accessor<EntityID[]>
}

/** Blocks that have at least one live parent relation (relation/block backlink). */
function useBlocksWithParentRelation(thread: ThreadOnlyCurrentNoDeleted): Accessor<ReadonlySet<EntityID>> {
	const relations = useLiveEntityCollection({ at: 'relation/block' }, ['relation/block'] as const, { thread, stripAtPrefix: true })
	return createMemo(() => {
		const set = new Set<EntityID>()
		for (const rec of relations().values()) {
			if (rec.block != null) set.add(String(rec.block))
		}
		return set
	})
}

const structuredTranscriptProvider: DerivedKidsProvider = {
	name: 'structuredTranscript',
	useDerivedKids(anchorID, thread) {
		const roots = useLiveEntityCollection(
			{ at: 'structuredTranscript/sourceEntity' },
			['structuredTranscript/sourceEntity', 'structuredTranscript/processedAt'] as const,
			{ thread, stripAtPrefix: true },
		)
		const trxRecording = useLiveEntityCollection(
			{ at: 'transcription/recording' },
			['transcription/recording'] as const,
			{ thread, stripAtPrefix: true },
		)
		const trxSourceEntity = useLiveEntityCollection(
			{ at: 'transcription/sourceEntity' },
			['transcription/sourceEntity'] as const,
			{ thread, stripAtPrefix: true },
		)
		const blocksWithParents = useBlocksWithParentRelation(thread)

		return createMemo(() => {
			const withParents = blocksWithParents()
			const kids: { id: EntityID; processedAt: string }[] = []
			for (const [rootID, rec] of roots()) {
				if (rootID === anchorID) continue
				if (withParents.has(rootID)) continue // real relations trump
				const trx = rec.sourceEntity != null ? String(rec.sourceEntity) : null
				if (!trx) continue
				const anchor = trxRecording().get(trx)?.recording ?? trxSourceEntity().get(trx)?.sourceEntity
				if (anchor !== anchorID) continue
				kids.push({ id: rootID, processedAt: String(rec.processedAt ?? '') })
			}
			kids.sort((a, b) => a.processedAt.localeCompare(b.processedAt) || a.id.localeCompare(b.id))
			return kids.map(k => k.id)
		})
	},
}

export const derivedKidsProviders: DerivedKidsProvider[] = [structuredTranscriptProvider]

/** Reactive derived kids of an anchor across all registered providers. */
export function useDerivedKids(anchorID: EntityID, thread: ThreadOnlyCurrentNoDeleted): Accessor<EntityID[]> {
	const perProvider = derivedKidsProviders.map(p => p.useDerivedKids(anchorID, thread))
	return createMemo(() => perProvider.flatMap(kids => kids()))
}
